import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FiX, FiTrash2, FiMinus, FiPlus, FiEdit2, FiAlertTriangle } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axios';
import { useQBCart } from '../../context/CartContext';
import { formatCurrency } from '../../utils/currency';
import { computeOptionsExtraPrice, splitMultiValueByKnownNames, localizeOptionsEntries, sanitizeOptionSelections } from '../../utils/bubbleTeaOptionPricing';
import { fetchQuickBitesMenu } from '../../api/bubbletea';
import { getLocalizedField } from '../../utils/localize';
import type { SupportedLanguage } from '../../i18n';
import type { BubbleTeaOptionCategory, BubbleTeaOptionItem } from '../../api/adminBubbleTeaOptions';

// ─── QB colour tokens ─────────────────────────────────────────────────────────

const QB_PRIMARY = '#1A9E8F';
const QB_PRIMARY_DARK = '#147D72';
const QB_LIGHT_BG = '#F0FAFA';

// ─── Styled components (mirrors CartDrawer pattern) ───────────────────────────

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 100%;
  max-width: 400px;
  background-color: var(--white);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;
  z-index: 1000;
  display: flex;
  flex-direction: column;

  @media (max-width: 400px) {
    max-width: 100%;
  }
`;

const DrawerHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--gray-medium);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DrawerTitle = styled.h2`
  font-size: 1.5rem;
  color: ${QB_PRIMARY};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--gray-dark);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  &:hover {
    color: var(--text-color);
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--gray-dark);
`;

const EmptyCartImage = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const CartItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
  border-bottom: 1px solid var(--gray-light);

  &:last-child {
    border-bottom: none;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const ItemName = styled.h4`
  font-size: 1rem;
  color: var(--text-color);
  flex: 1;
`;

const TypeBadge = styled.span`
  display: inline-block;
  background-color: ${QB_PRIMARY};
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  text-transform: uppercase;
  white-space: nowrap;
  margin-left: 0.5rem;
`;

const ItemOptions = styled.p`
  font-size: 0.85rem;
  color: var(--gray-dark);
  margin-bottom: 0.5rem;
`;

const ItemFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuantityButton = styled.button`
  background-color: var(--gray-light);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 1rem;

  &:hover {
    background-color: var(--gray-medium);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Quantity = styled.span`
  min-width: 32px;
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
`;

const ItemPrice = styled.p`
  font-size: 1rem;
  font-weight: bold;
  color: ${QB_PRIMARY};
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const PriceWarning = styled.span`
  display: flex;
  align-items: center;
  gap: 0.2rem;
  font-size: 0.7rem;
  color: #92620a;
  margin-top: 0.15rem;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--danger-color, #e74c3c);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #c0392b;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: ${QB_PRIMARY};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  &:hover { color: ${QB_PRIMARY_DARK}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const DrawerFooter = styled.div`
  padding: 1.5rem;
  border-top: 2px solid var(--gray-medium);
  background-color: var(--gray-light);
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.25rem;
  font-weight: bold;
  color: var(--text-color);
  margin-bottom: 1rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--gray-medium);
`;

const CheckoutButton = styled(Link)`
  display: block;
  width: 100%;
  padding: 1rem;
  background-color: ${QB_PRIMARY};
  color: var(--white);
  text-align: center;
  text-decoration: none;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
  box-sizing: border-box;

  &:hover {
    background-color: ${QB_PRIMARY_DARK};
  }
`;

const ContinueShoppingButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem;
  background-color: transparent;
  color: ${QB_PRIMARY};
  border: 1px solid ${QB_PRIMARY};
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 0.75rem;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${QB_PRIMARY};
    color: var(--white);
  }
`;

// ─── Modal styled components ──────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 480px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  z-index: 1101;
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem;
  color: ${QB_PRIMARY};
`;

const OptionSection = styled.div`
  margin-bottom: 1rem;
`;

const OptionLabel = styled.p`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 0.4rem 0.75rem;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.88rem;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${QB_PRIMARY};
    background: ${QB_LIGHT_BG};
  }
`;

// ─── Props ────────────────────────────────────────────────────────────────────

interface QBCartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const QBCartDrawer: React.FC<QBCartDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { qbItems, getQBCartTotal, removeQBItem, updateQBQuantity, replaceQBItemAtIndex } = useQBCart();
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx pattern: i18next's language can be a
  // region-qualified tag or unset, so narrow to the two v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';

  const [selectedBubbleTea, setSelectedBubbleTea] = useState<any | null>(null);
  const [optionSelections, setOptionSelections] = useState<Record<string, string>>({});
  const [editingQBCartIndex, setEditingQBCartIndex] = useState<number | null>(null);

  const { data: rawOptionCategories, isLoading: optionsLoading, isError: optionsError } = useQuery<BubbleTeaOptionCategory[]>({
    queryKey: ['bubbleTeaOptionCategories'],
    queryFn: async () => {
      const res = await axiosInstance.get<BubbleTeaOptionCategory[]>('/quick-bites/options/categories');
      return res.data;
    },
  });
  const optionCategories: BubbleTeaOptionCategory[] = Array.isArray(rawOptionCategories)
    ? rawOptionCategories.filter((c: BubbleTeaOptionCategory) => c.active)
    : [];

  // Per-product option catalog for the in-cart edit modal (task-028-s5-3).
  // The unscoped `optionCategories` query above stays as-is -- it's still
  // needed for the cart ROW's own already-selected-options line
  // (localizeOptionsEntries) and bubbleTeaPriceMayHaveChanged below, both of
  // which must reason about every item currently in the cart (potentially
  // several different products at once), not just whichever one is being
  // edited. This second query is scoped to the single product currently open
  // in the edit modal (selectedBubbleTea's menu_item_id) via the
  // GET /quick-bites/options/categories?menu_item_id=X filter (task-028
  // Sprint 3 subtask 4), so the modal only ever shows options actually
  // available for that product. The query key is `['bubbleTeaOptionCategories',
  // menuItemId]` -- deliberately a *longer* array than AccountOrders.tsx's
  // own unscoped `['bubbleTeaOptionCategories']` key (and than this file's
  // own unscoped query just above), so TanStack Query treats them as
  // entirely separate cache entries with no risk of collision.
  const editingMenuItemId: number | null = selectedBubbleTea?.menu_item_id ?? null;
  const { data: rawEditOptionCategories, isLoading: editOptionsLoading, isError: editOptionsError } = useQuery<BubbleTeaOptionCategory[]>({
    queryKey: ['bubbleTeaOptionCategories', editingMenuItemId],
    queryFn: async () => {
      const res = await axiosInstance.get<BubbleTeaOptionCategory[]>('/quick-bites/options/categories', {
        params: { menu_item_id: editingMenuItemId },
      });
      return res.data;
    },
    enabled: editingMenuItemId != null,
  });
  const editOptionCategories: BubbleTeaOptionCategory[] = Array.isArray(rawEditOptionCategories)
    ? rawEditOptionCategories.filter((c: BubbleTeaOptionCategory) => c.active)
    : [];

  const { data: qbMenu, isLoading: qbMenuLoading } = useQuery({ queryKey: ['quickBitesMenu'], queryFn: fetchQuickBitesMenu, enabled: isOpen });

  // Cart rows only ever store a flat English `name` snapshot (see
  // CartContext's QBCartItem shape) -- to show the localized zh name, re-resolve
  // the live menu item from the already-fetched qbMenu query by itemId+type
  // (same lookup precedent as bubbleTeaPriceMayHaveChanged/handleEditQBItem
  // above, used there for price reconciliation) and localize THAT. Falls back
  // to the cart's flat item.name when there's no live match (item removed
  // from the menu, or qbMenu hasn't loaded yet).
  const getDisplayName = (item: typeof qbItems[number]): string => {
    const menuItem = item.type === 'bubble_tea'
      ? qbMenu?.bubble_tea.find(bt => bt.menu_item_id === item.itemId)
      : qbMenu?.instant_cakes.find(ic => ic.id === item.itemId);
    return menuItem ? (getLocalizedField(menuItem, 'name', language) as string) : item.name;
  };

  // Detection-only price reconciliation: compares each bubble_tea cart item's
  // stored price against what would be computed today from the freshly-fetched
  // menu + option categories (e.g. an admin may have deactivated a previously
  // selected option). Never mutates item.price or the cart total — display only.
  // Fails open (no warning) while either query is loading/erroring.
  const bubbleTeaPriceMayHaveChanged = (item: typeof qbItems[number]): boolean => {
    if (item.type !== 'bubble_tea') return false;
    if (optionsLoading || qbMenuLoading || optionsError) return false;
    const menuItem = qbMenu?.bubble_tea.find(bt => bt.menu_item_id === item.itemId);
    if (!menuItem) return true;
    const recomputedPrice = menuItem.price + computeOptionsExtraPrice(item.options, optionCategories);
    return Math.abs(recomputedPrice - item.price) > 0.005;
  };

  const handleEditQBItem = (index: number) => {
    // Editing requires the true base menu price (qbMenu) to recompute the option-price
    // total correctly. cartItem.price already has any previously-selected option extras
    // baked in, so falling back to it here as a "base" would double-count those extras
    // once computeOptionsExtraPrice runs again in confirmEditBubbleTea. Block the edit
    // until qbMenu has loaded rather than risk that silent overcharge.
    if (qbMenuLoading) return;
    const cartItem = qbItems[index];
    if (!cartItem || cartItem.type !== 'bubble_tea') return;
    // If the item is no longer on the loaded menu, we have no true base price to
    // recompute from (cartItem.price already bakes in prior option extras), so
    // block the edit entirely rather than risk double-counting those extras.
    const menuItem = qbMenu?.bubble_tea.find(bt => bt.menu_item_id === cartItem.itemId);
    if (!menuItem) return;
    setOptionSelections(cartItem.options ? { ...cartItem.options } : {});
    setEditingQBCartIndex(index);
    setSelectedBubbleTea(menuItem);
  };

  const confirmEditBubbleTea = () => {
    if (!selectedBubbleTea || editingQBCartIndex === null) return;
    if (editOptionsError && editOptionCategories.length === 0) return;
    const sanitizedSelections = sanitizeOptionSelections(optionSelections, editOptionCategories);
    const finalPrice = selectedBubbleTea.price + computeOptionsExtraPrice(sanitizedSelections, editOptionCategories);
    const newOptions = Object.keys(sanitizedSelections).length > 0
      ? { ...sanitizedSelections }
      : undefined;
    const existing = qbItems[editingQBCartIndex];
    replaceQBItemAtIndex(editingQBCartIndex, {
      type: 'bubble_tea',
      itemId: selectedBubbleTea.menu_item_id,
      name: selectedBubbleTea.name,
      price: finalPrice,
      quantity: existing ? existing.quantity : 1,
      options: newOptions,
    });
    setSelectedBubbleTea(null);
    setOptionSelections({});
    setEditingQBCartIndex(null);
  };

  const cancelEdit = () => {
    setSelectedBubbleTea(null);
    setOptionSelections({});
    setEditingQBCartIndex(null);
  };

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <DrawerContainer $isOpen={isOpen} data-testid="qb-cart-drawer">
        <DrawerHeader>
          <DrawerTitle>{t('qbCartDrawer.title', { count: qbItems.length })}</DrawerTitle>
          <CloseButton onClick={onClose} aria-label={t('qbCartDrawer.closeCartAriaLabel')}>
            <FiX />
          </CloseButton>
        </DrawerHeader>

        <DrawerContent>
          {qbItems.length === 0 ? (
            <EmptyCart>
              <EmptyCartImage>🧋</EmptyCartImage>
              <h3>{t('qbCartDrawer.emptyTitle')}</h3>
              <p>{t('qbCartDrawer.emptySubtitle')}</p>
            </EmptyCart>
          ) : (
            qbItems.map((item, index) => (
              <CartItem key={`${item.type}-${item.itemId}-${index}`}>
                <ItemHeader>
                  <ItemName>
                    {getDisplayName(item)}
                    <TypeBadge>
                      {item.type === 'bubble_tea' ? t('qbCartDrawer.typeBubbleTea') : t('qbCartDrawer.typeInstantCake')}
                    </TypeBadge>
                  </ItemName>
                  {item.type === 'bubble_tea' && (
                    <EditButton
                      aria-label={t('qbCartDrawer.editItemAriaLabel', { name: item.name })}
                      onClick={() => handleEditQBItem(index)}
                      disabled={qbMenuLoading || !qbMenu?.bubble_tea.some(bt => bt.menu_item_id === item.itemId)}
                    >
                      <FiEdit2 size={14} />
                    </EditButton>
                  )}
                  <RemoveButton
                    aria-label={t('qbCartDrawer.removeItemAriaLabel', { name: item.name })}
                    onClick={() => removeQBItem(item.itemId, item.type)}
                  >
                    <FiTrash2 />
                  </RemoveButton>
                </ItemHeader>

                {item.type === 'bubble_tea' && item.options && Object.keys(item.options).length > 0 && (
                  <ItemOptions>{localizeOptionsEntries(item.options, optionCategories, language).map(({ key, value }) => `${key}: ${value}`).join(', ')}</ItemOptions>
                )}

                <ItemFooter>
                  <QuantityControls>
                    <QuantityButton
                      onClick={() => updateQBQuantity(item.itemId, item.type, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      <FiMinus />
                    </QuantityButton>
                    <Quantity>{item.quantity}</Quantity>
                    <QuantityButton
                      onClick={() => updateQBQuantity(item.itemId, item.type, item.quantity + 1)}
                    >
                      <FiPlus />
                    </QuantityButton>
                  </QuantityControls>
                  <PriceBlock>
                    <ItemPrice>{formatCurrency(item.price * item.quantity)}</ItemPrice>
                    {bubbleTeaPriceMayHaveChanged(item) && (
                      <PriceWarning data-testid={`qb-cart-price-warning-${index}`}>
                        <FiAlertTriangle size={11} />
                        {t('qbCartDrawer.priceMayHaveChanged')}
                      </PriceWarning>
                    )}
                  </PriceBlock>
                </ItemFooter>
              </CartItem>
            ))
          )}
        </DrawerContent>

        {qbItems.length > 0 && (
          <DrawerFooter>
            <TotalRow>
              <span>{t('qbCartDrawer.totalLabel')}</span>
              <span>{formatCurrency(getQBCartTotal())}</span>
            </TotalRow>
            <CheckoutButton to="/quick-bites/checkout" onClick={onClose}>
              {t('qbCartDrawer.proceedToCheckout')}
            </CheckoutButton>
            <ContinueShoppingButton onClick={() => { navigate('/quick-bites'); onClose(); }}>
              {t('qbCartDrawer.continueShopping')}
            </ContinueShoppingButton>
          </DrawerFooter>
        )}
      </DrawerContainer>

      {selectedBubbleTea && (
        <ModalOverlay onClick={cancelEdit}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{getLocalizedField(selectedBubbleTea, 'name', language) as string}</ModalTitle>
            {editOptionsLoading && <p>{t('qbCartDrawer.modal.loadingOptions')}</p>}
            {editOptionsError && editOptionCategories.length === 0 && <p>{t('qbCartDrawer.modal.optionsLoadError')}</p>}
            {!editOptionsLoading && editOptionCategories.map((cat: BubbleTeaOptionCategory) => {
              const activeItems: BubbleTeaOptionItem[] = (cat.items || []).filter((i: BubbleTeaOptionItem) => i.active).sort((a: BubbleTeaOptionItem, b: BubbleTeaOptionItem) => a.sort_order - b.sort_order);
              return (
                <OptionSection key={cat.id}>
                  <OptionLabel>{getLocalizedField(cat, 'name', language) as string}</OptionLabel>
                  <OptionsGrid>
                    {cat.selection_mode !== 'multiple' ? (
                      activeItems.map((optItem: BubbleTeaOptionItem) => (
                        <OptionRow
                          key={optItem.id}
                          style={optionSelections[cat.name] === optItem.name ? { borderColor: QB_PRIMARY, background: QB_LIGHT_BG } : {}}
                        >
                          <input
                            type="radio"
                            name={cat.name}
                            value={optItem.name}
                            checked={optionSelections[cat.name] === optItem.name}
                            onChange={() => setOptionSelections(prev => ({ ...prev, [cat.name]: optItem.name }))}
                          />
                          {getLocalizedField(optItem, 'name', language) as string}
                        </OptionRow>
                      ))
                    ) : (
                      activeItems.map((optItem: BubbleTeaOptionItem) => {
                        const currentToppings = optionSelections[cat.name] ? splitMultiValueByKnownNames(optionSelections[cat.name], cat.items).map((t: string) => t.trim()) : [];
                        const checked = currentToppings.includes(optItem.name);
                        return (
                          <OptionRow
                            key={optItem.id}
                            style={checked ? { borderColor: QB_PRIMARY, background: QB_LIGHT_BG } : {}}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const newToppings = checked
                                  ? currentToppings.filter((t: string) => t !== optItem.name)
                                  : [...currentToppings, optItem.name];
                                setOptionSelections(prev => ({ ...prev, [cat.name]: newToppings.join(',') }));
                              }}
                            />
                            {getLocalizedField(optItem, 'name', language) as string}
                          </OptionRow>
                        );
                      })
                    )}
                  </OptionsGrid>
                </OptionSection>
              );
            })}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                onClick={confirmEditBubbleTea}
                disabled={editOptionsLoading || (editOptionsError && editOptionCategories.length === 0)}
                style={{ flex: 1, padding: '0.6rem', background: QB_PRIMARY, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
              >
                {t('qbCartDrawer.modal.updateItem')}
              </button>
              <button
                onClick={cancelEdit}
                style={{ padding: '0.6rem 1rem', background: 'transparent', color: QB_PRIMARY, border: `2px solid ${QB_PRIMARY}`, borderRadius: '8px', cursor: 'pointer' }}
              >
                {t('qbCartDrawer.modal.cancel')}
              </button>
            </div>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
};

export default QBCartDrawer;
